import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from '../../api/axios';
import endpoints from '../../api/endpoints';
import { getUserProfile } from '../../services/UserService';
import { parse } from 'date-fns';

const initialState = {
  user: null,
  userProfile: null,
  isAuthenticated: false,
  loading: false,
  authLoaded: false,
  authLoading: false,
  error: null,
};

const parseError = (error) => {
  const responseMessage = error.response?.data?.error;
  return {
    message: responseMessage || error.message || 'Unexpected error',
    status: error.response?.status || 500,
  };
};

const ignorableMessages = [
  'Cannot invoke "jakarta.servlet.http.Cookie.getValue()"',
  'User is not logged in!',
  'Request failed with status code 400',
  'uri=/auth/refreshtoken',
  'Refresh token is not in database!',
  'Refresh Token is empty!',
];

const isIgnorableError = (message) =>
  ignorableMessages.some((ignorable) => message?.includes(ignorable));

export const checkAuth = createAsyncThunk('auth/checkAuth', async (_, thunkAPI) => {
  try {
    const res = await axios.get(endpoints.user.me); // user object same with login response
    const profileRes = await axios.get(endpoints.user.profile.replace('{userId}', res.data.id));
    console.log("authSlice checkAuth ", {res, profileRes});
    return { user: res.data, userProfile: profileRes.data };
  } catch (error) {
    console.log("authSlice checkAuth error", error.response.data.message)
    const parsedError = parseError(error);

    console.log("checkAuth parsedError", parsedError)
    if (isIgnorableError(parsedError.message)) {
      console.warn('Ignored checkAuth error:', parsedError.message);
      thunkAPI.dispatch(logout()); // optional if you want to reset session silently
      return thunkAPI.fulfillWithValue(null);
    }

    // Unexpected error — reject with value (can be caught by ErrorBoundary if unhandled)

    return thunkAPI.rejectWithValue(parsedError);
  }
});

export const login = createAsyncThunk('auth/login', async (credentials, thunkAPI) => {
  try {
    const res = await axios.post(endpoints.auth.login, credentials);
    const profileRes = await axios.get(endpoints.user.profile.replace('{userId}', res.data.id));

    console.log("authSlice login ", {res, profileRes});
    return { user: res.data, userProfile: profileRes.data };
  } catch (error) {
    console.log("authSlice login error", error);
    return thunkAPI.rejectWithValue(parseError(error));
  }
});

export const logout = createAsyncThunk('auth/logout', async (_, thunkAPI) => {
  try {
    await axios.post(endpoints.auth.logout);
  } catch (error) {
    // ignore error
    console.log("authSlice Logout error", error);
  }
  return;
});

export const isOnboardingCompleted = (state) => !!state.auth.user?.onboardingCompleted;

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setUser: (state, action) => {
      state.user = action.payload;
      state.isAuthenticated = !!action.payload;
    },
    clearUser: (state) => {
      state.user = null;
      state.isAuthenticated = false;
    },
    clearError: (state) => {
        state.error = null;
    },
    updateOnboarding: (state, action) => {
      const { onboardingCompleted, currentOnboardingStep, fullOnboardingProgress } = action.payload;

      if (state.user) {
        state.user.onboardingCompleted = onboardingCompleted;
        state.user.currentOnboardingStep = currentOnboardingStep;
        state.user.fullOnboardingProgress = fullOnboardingProgress;
      }
    },
    //check later where setAuthLoading is used or of necessary
    setAuthLoading: (state, action) => { // ✅ NEW
      state.authLoading = action.payload;
    },
    setUserProfile: (state, action) => {
      state.userProfile = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
        //Check Auth
      .addCase(checkAuth.pending, (state) => {
        state.loading = true;
        state.authLoaded = false;
      })
      .addCase(checkAuth.fulfilled, (state, action) => {
        if (action.payload) {
          state.user = action.payload.user;
          state.userProfile = action.payload.userProfile;
          state.isAuthenticated = true;
        } else {
          state.user = null;
          state.profile = null;
          state.isAuthenticated = false;
        }
        state.loading = false;
        state.authLoaded = true;
        state.error = null;
      })
      .addCase(checkAuth.rejected, (state, action) => {
        state.user = null;
        state.isAuthenticated = false;
        state.loading = false;
        state.authLoaded = true;
        if (action.payload) {
          state.error = action.payload; // only store real errors
        } else {
          state.error = null; // no error if payload is null (was ignorable)
        }
      })

      //LOGIN
      .addCase(login.pending, (state) => {
        state.authLoading = true;
        state.error = null;
        state.authLoaded = false;
        state.loading = false;
      })
      .addCase(login.fulfilled, (state, action) => {
        state.user = action.payload.user;
        state.userProfile = action.payload.userProfile;
        state.isAuthenticated = true;
        state.authLoading = false;
        state.authLoaded = true;
        state.error = null; //
        state.loading = false;
      })
      .addCase(login.rejected, (state, action) => {
        state.authLoading = false;
        state.user = null;
        state.isAuthenticated = false;
        state.authLoaded = true;
        state.error = action.payload;
        state.loading = false;
      })

      //LOGOUT
      .addCase(logout.fulfilled, (state) => {
        authSlice.caseReducers.clearUser(state);
        state.authLoading = false;
        state.authLoaded = true; 
        state.loading = false;
      })
      .addCase(logout.rejected, (state, action) => {
        // Log the error if necessary, but mark loading state as finished
        console.error("Logout failed", action.error);
        authSlice.caseReducers.clearUser(state);
        state.authLoading = false;
        state.authLoaded = true;
        state.loading = false;
      });
  },
});

export const { 
  setUser, 
  clearUser, 
  clearError, 
  setAuthLoading,
  updateOnboarding,
  setUserProfile
} = authSlice.actions;
export default authSlice.reducer;

// For later implementation
export const selectAuth = (state) => state.auth;
export const selectIsAuthenticated = (state) => state.auth.isAuthenticated;
export const selectUser = (state) => state.auth.user;
export const selectAuthLoading = (state) => state.auth.authLoading;
